<template>
  <div>
    <FormKit
      type="form"
      name="Kündigung"
      id="Kündigung"
      ref="cancelContractForm"
      :incomplete-message="false"
      :actions="false"
      @submit="submitCancelContract"
      #default="{ state: { valid } }"
      form-class="dbc-formkit-form"
    >
      <div class="dbc-formkit-form-inner">
        <div>
          <FormKit
            type="dbcText"
            name="firstName"
            :disabled="state === 'validation'"
            :label="$t('dbc-cancel-contract-input-firstname-label')"
            :placeholder="$t('dbc-cancel-contract-input-firstname-placeholder')"
            validation="required"
          />
          <FormKit
            type="dbcText"
            name="lastName"
            :disabled="state === 'validation'"
            :label="$t('dbc-cancel-contract-input-lastname-label')"
            :placeholder="$t('dbc-cancel-contract-input-lastname-placeholder')"
            validation="required"
          />
          <FormKit
            type="dbcEmail"
            name="email"
            :disabled="state === 'validation'"
            :label="$t('dbc-cancel-contract-input-email-label')"
            :help="$t('dbc-cancel-contract-input-email-subline')"
            validation="required|email"
            :validation-messages="{
              email: $t('dbc-error-email-validation'),
            }"
            :placeholder="$t('dbc-cancel-contract-input-email-placeholder')"
          />
          <FormKit
            type="dbcText"
            name="customerNumber"
            :disabled="state === 'validation'"
            :label="$t('dbc-cancel-contract-input-number-label')"
            :placeholder="$t('dbc-cancel-contract-input-number-placeholder')"
            :help="$t('dbc-cancel-contract-input-number-subline')"
            :validation="[['required'], ['matches', /(\d{10})|(\w{2}-\w+)/]]"
            :validation-messages="{
              matches: $t('dbc-error-customer-number-validation'),
            }"
          />
        </div>
        <div>
          <FormsCancelContractReasons
            :state="state"
            @onReasonChanged="onReasonChanged"
          />

          <FormKit
            type="textarea"
            name="textarea"
            :label="textareaLabel"
            :disabled="state === 'validation'"
            rows="10"
            maxlength="500"
            :validation="textareaValidation"
            :placeholder="
              $t('dbc-cancel-contract-textarea-message-placeholder')
            "
            input-class="resize-none"
            v-model="textareaValue"
          >
            <template #label>
              <div class="flex justify-between">
                <div>
                  {{ textareaLabel }}
                  <span>{{
                    textareaValidation === 'required' ? '*' : ''
                  }}</span>
                </div>
                <div class="formkit-help">{{ textareaValue.length }}/500</div>
              </div>
            </template>
          </FormKit>
        </div>
      </div>

      <UtilityText
        v-if="errorCode !== ''"
        class="dbc-u-error-txt"
        :text="$t(errorCode)"
      />

      <div class="flex flex-col gap-[2rem]">
        <div class="dbc-btn-group">
          <FormKit
            data-test="nextButton"
            type="button"
            :label="
              $t(
                state === 'validation'
                  ? 'dbc-cancel-contract-button-edit'
                  : 'dbc-cancel-contract-button-next'
              )
            "
            input-class="dbc-gc-btn dbc-gc-btn--ghost dbc-gc-form__button"
            :disabled="!valid || isLoading"
            @click="() => $emit('onClickNext')"
          />
          <FormKit
            v-if="state === 'validation'"
            :prefix-icon="!isLoading ? 'submitArrow' : ''"
            type="submit"
            :label="$t('dbc-cancel-contract-button-request-cancellation')"
            :loading="isLoading"
            :disabled="isLoading"
            @click="onClickRequestCancelContract"
            input-class="dbc-gc-btn dbc-gc-btn--primary dbc-btn--icon dbc-gc-form__button dbc-cancel-contract-button-request-cancellation"
          />
        </div>
        <UtilityButton
          v-if="state === 'validation'"
          :button="{
            type: 'secondary',
            link: getPagePath['home']![locale]!,
            text: $t('dbc-cancel-contract-button-cancel'),
          }"
        />
      </div>
    </FormKit>
  </div>
</template>

<script setup lang="ts">
defineProps<{
  state: CancelContractStates
}>()

const emit = defineEmits<{
  (
    e: 'onClickRequestCancelContract',
    state: CancelContractStates,
    formData: CancelContractData
  ): void
  (e: 'onClickNext'): void
}>()

const { t, locale } = useI18n()

const cancelContractForm = ref(undefined)

function onClickRequestCancelContract() {
  //@ts-ignore
  cancelContractForm.value?.node.submit()
}

const textareaValidation = ref<string>('')
const textareaLabel = ref<string>(
  t('dbc-cancel-contract-textarea-message-label')
)
const textareaValue = ref<string>('')

function onReasonChanged(reason: CancelContractReason) {
  textareaValidation.value = reason.commentMandatory ? 'required' : ''
}

const isLoading = ref(false)

const errorCode = ref('')

async function submitCancelContract(formData: CancelContractData) {
  isLoading.value = true

  const response = await fetchCancelContract(formData)

  isLoading.value = false
  let newState: CancelContractStates = 'error'
  if (response._data?.data?.errorCode) {
    errorCode.value = response._data.data.errorCode
    return
  } else if (!response.ok) {
    newState = 'error'
  }
  if (response.ok && response.status === 200) newState = 'success'
  emit('onClickRequestCancelContract', newState, formData)
  return response._data
}
</script>
